import React, { useState } from 'react'
import { formatDistanceToNow, format } from 'date-fns'
import { 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Copy, 
  Eye, 
  Share2, 
  Calendar,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { SocialPost, SocialProfile, Platform } from '@/types/social'

interface PostListProps {
  posts: SocialPost[]
  profiles: SocialProfile[]
  onEdit?: (post: SocialPost) => void
  onDelete?: (postId: string) => void
  onDuplicate?: (post: SocialPost) => void
  onViewAnalytics?: (post: SocialPost) => void
}

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

const STATUS_ICONS = {
  DRAFT: Clock,
  SCHEDULED: Calendar,
  PUBLISHED: CheckCircle,
  FAILED: XCircle,
  CANCELLED: AlertCircle
}

const STATUS_COLORS = {
  DRAFT: 'text-muted-foreground',
  SCHEDULED: 'text-blue-600',
  PUBLISHED: 'text-green-600',
  FAILED: 'text-destructive',
  CANCELLED: 'text-orange-600'
}

export default function PostList({ 
  posts, 
  profiles, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onViewAnalytics 
}: PostListProps) {
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set())

  const toggleExpanded = (postId: string) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(postId)) {
        newSet.delete(postId)
      } else {
        newSet.add(postId)
      }
      return newSet
    })
  }

  const getProfile = (profileId: string | null) => {
    return profiles.find(p => p.id === profileId)
  }

  const getPlatformFromProfile = (profileId: string | null): Platform => {
    const profile = getProfile(profileId)
    return profile?.platform || 'INSTAGRAM'
  }

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const formatScheduledTime = (scheduledTime: string | null) => {
    if (!scheduledTime) return null
    const date = new Date(scheduledTime)
    return format(date, 'MMM d, yyyy \'at\' h:mm a')
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <Share2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No posts found</h3>
        <p className="text-muted-foreground mb-4">
          Create your first social media post to get started.
        </p>
        <Button>Create Post</Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {posts.map(post => {
        const profile = getProfile(post.profile_id)
        const platform = getPlatformFromProfile(post.profile_id)
        const StatusIcon = STATUS_ICONS[post.status]
        const isExpanded = expandedPosts.has(post.id)
        const displayContent = isExpanded ? post.content : truncateText(post.content)

        return (
          <Card key={post.id} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                {/* Main Content */}
                <div className="flex-1 space-y-3">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[platform]}`} />
                    <span className="font-medium text-sm">
                      {profile?.display_name || profile?.name || 'Unknown Profile'}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {platform}
                    </Badge>
                    <StatusIcon className={`h-4 w-4 ${STATUS_COLORS[post.status]}`} />
                    <Badge 
                      variant={post.status === 'PUBLISHED' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {post.status}
                    </Badge>
                    {post.ai_generated && (
                      <Badge variant="outline" className="text-xs">
                        AI Generated
                      </Badge>
                    )}
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <p className="text-sm leading-relaxed">
                      {displayContent}
                      {post.content.length > 150 && (
                        <button
                          onClick={() => toggleExpanded(post.id)}
                          className="ml-2 text-primary hover:underline text-xs"
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </p>

                    {post.caption && isExpanded && (
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Caption:</p>
                        <p className="text-sm">{post.caption}</p>
                      </div>
                    )}

                    {/* Hashtags and Tags */}
                    {(post.hashtags || post.tags) && (
                      <div className="flex flex-wrap gap-1">
                        {post.hashtags?.slice(0, 5).map(hashtag => (
                          <Badge key={hashtag} variant="secondary" className="text-xs">
                            {hashtag}
                          </Badge>
                        ))}
                        {post.tags?.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {((post.hashtags?.length || 0) + (post.tags?.length || 0)) > 8 && (
                          <Badge variant="outline" className="text-xs">
                            +{((post.hashtags?.length || 0) + (post.tags?.length || 0)) - 8} more
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Media Preview */}
                    {post.media && post.media.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {post.media.slice(0, 4).map((item, index) => (
                          <img
                            key={item.id}
                            src={item.url}
                            alt={item.alt_text || `Media ${index + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border"
                          />
                        ))}
                        {post.media.length > 4 && (
                          <div className="w-16 h-16 bg-muted rounded-lg border flex items-center justify-center text-xs text-muted-foreground">
                            +{post.media.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        Created {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </span>
                      {post.scheduled_time && post.status === 'SCHEDULED' && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatScheduledTime(post.scheduled_time)}
                        </span>
                      )}
                      {post.published_at && post.status === 'PUBLISHED' && (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Published {formatDistanceToNow(new Date(post.published_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>

                    {/* Engagement Metrics */}
                    {post.engagement_metrics && post.status === 'PUBLISHED' && (
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {post.engagement_metrics.likes && (
                          <span>{post.engagement_metrics.likes} likes</span>
                        )}
                        {post.engagement_metrics.comments && (
                          <span>{post.engagement_metrics.comments} comments</span>
                        )}
                        {post.engagement_metrics.shares && (
                          <span>{post.engagement_metrics.shares} shares</span>
                        )}
                        {post.engagement_metrics.reach && (
                          <span>{post.engagement_metrics.reach} reach</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => onEdit?.(post)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDuplicate?.(post)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggleExpanded(post.id)}>
                      <Eye className="h-4 w-4 mr-2" />
                      {isExpanded ? 'Collapse' : 'View Details'}
                    </DropdownMenuItem>
                    {post.status === 'PUBLISHED' && (
                      <DropdownMenuItem onClick={() => onViewAnalytics?.(post)}>
                        <BarChart3 className="h-4 w-4 mr-2" />
                        View Analytics
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => onDelete?.(post.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}